import React, { useState, useEffect } from 'react'

// components
import { useTranslation } from 'react-i18next'

import {
  Grid,
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useDataApi } from '../../context/DataApiContext'
import { useConfirmationDialogProvider } from '../../context/ConfirmationDialogContext'
import useStyles from './styles'

import CreateVehicleTypeDialog from '../../components/VehicleType/components/CreateVehicleTypeDialog'
import ModifyVehicleTypeDialog from '../../components/VehicleType/components/ModifyVehicleTypeDialog'
import Table from '../../components/Table'
import generateColumns from './columns'

export default function VehicleTypesList({ history }) {
  const { t } = useTranslation()

  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const { showConfirmationDialog } = useConfirmationDialogProvider()

  const [loading, setLoading] = useState(true)
  const [tableState, setTableState] = useState({
    page: 0, sort: null, filters: null,
  })
  const [tableData, setTableData] = useState(null)

  const [openCreateVehicleTypeDialog, setOpenCreateVehicleTypeDialog] = useState(false)
  const [openModifyVehicleTypeDialog, setOpenModifyVehicleTypeDialog] = useState(false)
  const [vehicleTypeToModify, setVehicleTypeToModify] = useState(false)

  const refreshData = (keepPage = false) => {
    if (keepPage) {
      setTableState({ ...tableState })
    } else {
      setTableState({ page: 0, sort: null, filters: null })
    }
  }

  const resource = 'vehicle-type'

  useEffect(() => {
    dataProvider.getList(
      resource,
      {
        range: { page: tableState.page + 1 },
        filters: tableState.filters,
        sort: tableState.sort,
      },
    ).then((response) => {
      setTableData(response.data)
      setLoading(false)
    }).catch(() => showNotification('error', t('errors.api-error')))
    // eslint-disable-next-line
  }, [tableState])

  const onModify = (rowIndex) => {
    setVehicleTypeToModify(tableData.items[rowIndex])
    setOpenModifyVehicleTypeDialog(true)
  }

  const onSee = (rowIndex) => {
    const { id } = tableData.items[rowIndex]
    history.push(`/vehicle-types/items/${id}`)
  }

  const onDeactivate = (rowIndex) => {
    const { id, type, specificity } = tableData.items[rowIndex]
    showConfirmationDialog(
      t('vehicle-types-list.confirm-vehicle-type-deletion', { type, specificity }),
      () => {
        dataProvider.post(`vehicle-type/${id}/deactivate`, {})
          .then(() => {
            refreshData()
            showNotification('success', t('vehicle-types-list.deactivate-success'))
          })
          .catch(() => showNotification('error', t('errors.api-error')))
      },
      t('common.deactivate'),
    )
  }

  const tableColumns = generateColumns({
    t,
    classes,
    onDeactivate,
    onModify,
    onSee,
  })

  if (loading) {
    return <Loading />
  }
  return (
    <>
      <CreateVehicleTypeDialog
        open={openCreateVehicleTypeDialog}
        onClose={() => setOpenCreateVehicleTypeDialog(false)}
        onCreated={() => {
          refreshData()
          setOpenCreateVehicleTypeDialog(false)
        }}
      />
      <ModifyVehicleTypeDialog
        open={openModifyVehicleTypeDialog}
        onClose={() => {
          setOpenModifyVehicleTypeDialog(false)
          setVehicleTypeToModify({})
        }}
        originalVehicleType={vehicleTypeToModify}
        onModified={() => {
          refreshData(true)
          setOpenModifyVehicleTypeDialog(false)
        }}
      />
      <Grid container alignItems="center" justify="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t('vehicle-types-list.title')} />
        </Grid>
        <Grid item container sm={8} justify="flex-end">
          <Button
            variant="contained"
            color="primary"
            fullWidth={fullWidth}
            onClick={() => setOpenCreateVehicleTypeDialog(true)}
          >
            {t('create-vehicle-type-dialog.title')}
          </Button>
        </Grid>
      </Grid>
      <Table
        id={resource}
        columns={tableColumns}
        data={tableData}
        page={tableState.page}
        sort={tableState.sort}
        onChangePage={(currentPage) => {
          setTableState({ ...tableState, page: currentPage })
        }}
        onColumnSortChange={(changedColumn, direction) => {
          const newSort = {
            field: changedColumn,
            direction: direction.toUpperCase(),
          }
          setTableState({ ...tableState, sort: newSort })
        }}
      />
    </>
  )
}
